/*
DECOMPOSITION (key lies along X; box 1.6 x 0.55 x 0.22 m)

Outer shell (go first)
- barrel (shell, hinge, delay 0): ink housing around the shaft, offset down.
  - barrel_shell: ink lathe tube with magenta bands and a hinge lug.
  - barrel_bearing: two brushed-steel races inside the shell.
- bow (shell, lift, delay 0.05): rounded candy-pink head with ink outline and a see-through hole.
  - bow_ring: magenta sticker-dot ring in the hole.
  - bow_face: cream face plate with a lime status pin.

Mechanism
- collar (spin, delay 0.15): tangerine ring near the bow, with a magenta liner.
  - collar_segments: 4 tangerine arc pieces.
- bit (slide, delay 0.3): toothed block at the tip.
  - bit_body: ink outline slab plus pink block.
  - teeth: 5 cut teeth in a row (ink + pink each).
- shaft (slide, delay 0.45): straight bar.
  - shaft_sleeve: pink tube with ink end bands.
  - shaft_core: brushed-steel rod running through to the bit.

Electronics and pins (inner, last)
- pin_stack (lift, delay 0.7): the lime status pins inside the bit.
  - pins: 5 lime pins, each with a steel tip and an ink cap, kept in a row.

Behaviour: scroll spins the collar; action turns the shaft and bobs the pins as the teeth click past.
Fasteners: none. The pieces are held by fit, like a toy.
*/
import { useMemo } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

const mkCanvas = (s) => {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  return c;
};

function heightToNormal(c, strength) {
  const ctx = c.getContext('2d');
  const w = c.width, h = c.height;
  const src = ctx.getImageData(0, 0, w, h).data;
  const out = ctx.createImageData(w, h);
  const H = (x, y) => src[(((y + h) % h) * w + ((x + w) % w)) * 4] / 255;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (H(x + 1, y) - H(x - 1, y)) * strength;
      const dy = (H(x, y + 1) - H(x, y - 1)) * strength;
      const l = Math.sqrt(dx * dx + dy * dy + 1);
      const i = (y * w + x) * 4;
      out.data[i] = ((-dx / l) * 0.5 + 0.5) * 255;
      out.data[i + 1] = ((dy / l) * 0.5 + 0.5) * 255;
      out.data[i + 2] = ((1 / l) * 0.5 + 0.5) * 255;
      out.data[i + 3] = 255;
    }
  }
  ctx.putImageData(out, 0, 0);
  return c;
}

function makeGrain() {
  const c = mkCanvas(128);
  if (!c) return null;
  const ctx = c.getContext('2d');
  const id = ctx.createImageData(128, 128);
  for (let i = 0; i < 128 * 128; i++) {
    const v = 110 + Math.random() * 90;
    id.data[i * 4] = id.data[i * 4 + 1] = id.data[i * 4 + 2] = v;
    id.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(id, 0, 0);
  heightToNormal(c, 1.2);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(22, 22);
  return t;
}

function makeBrushed() {
  const c = mkCanvas(128);
  if (!c) return null;
  const ctx = c.getContext('2d');
  for (let y = 0; y < 128; y++) {
    const v = 90 + Math.random() * 120;
    ctx.fillStyle = `rgb(${v},${v},${v})`;
    ctx.fillRect(0, y, 128, 1);
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(1, 3);
  return t;
}

const GRAIN = makeGrain();
const BRUSHED = makeBrushed();

function makeMats() {
  const plastic = (color, extra) =>
    new THREE.MeshPhysicalMaterial({
      color,
      roughness: 0.38,
      metalness: 0,
      clearcoat: 1,
      clearcoatRoughness: 0.15,
      normalMap: GRAIN || null,
      normalScale: new THREE.Vector2(0.25, 0.25),
      transparent: true,
      emissive: new THREE.Color('#ff5a9a'),
      emissiveIntensity: 0,
      ...extra,
    });
  return {
    pink: plastic('#F7A8C8'),
    ink: plastic('#1A1416', { roughness: 0.5, clearcoat: 0.5 }),
    tang: plastic('#FF5A1F'),
    lime: plastic('#B8F04A'),
    mag: plastic('#E6197C'),
    cream: plastic('#FFF6EC'),
    steel: new THREE.MeshPhysicalMaterial({
      color: '#c9c4c8',
      metalness: 1,
      roughness: 0.7,
      roughnessMap: BRUSHED || null,
      transparent: true,
      side: THREE.DoubleSide,
      emissive: new THREE.Color('#ff5a9a'),
      emissiveIntensity: 0,
    }),
  };
}

function ringGeo(R, rh, depth, bev) {
  const sh = new THREE.Shape();
  sh.absarc(0, 0, R - bev, 0, Math.PI * 2, false);
  const h = new THREE.Path();
  h.absarc(0, 0, rh + bev, 0, Math.PI * 2, true);
  sh.holes.push(h);
  const d = depth - 2 * bev;
  const g = new THREE.ExtrudeGeometry(sh, {
    depth: d, bevelEnabled: true, bevelSize: bev, bevelThickness: bev,
    bevelSegments: 3, curveSegments: 40,
  });
  g.translate(0, 0, -d / 2);
  return g;
}

function roundedBox(w, h, d, r, b) {
  const ww = w - 2 * b, hh = h - 2 * b, x = -ww / 2, y = -hh / 2;
  const sh = new THREE.Shape();
  sh.moveTo(x + r, y);
  sh.lineTo(x + ww - r, y);
  sh.quadraticCurveTo(x + ww, y, x + ww, y + r);
  sh.lineTo(x + ww, y + hh - r);
  sh.quadraticCurveTo(x + ww, y + hh, x + ww - r, y + hh);
  sh.lineTo(x + r, y + hh);
  sh.quadraticCurveTo(x, y + hh, x, y + hh - r);
  sh.lineTo(x, y + r);
  sh.quadraticCurveTo(x, y, x + r, y);
  const dd = d - 2 * b;
  const g = new THREE.ExtrudeGeometry(sh, {
    depth: dd, bevelEnabled: true, bevelSize: b, bevelThickness: b,
    bevelSegments: 2, curveSegments: 6,
  });
  g.translate(0, 0, -dd / 2);
  return g;
}

// lathe whose axis is X
function lathe(pts, seg = 32) {
  const g = new THREE.LatheGeometry(pts.map((p) => new THREE.Vector2(p[0], p[1])), seg);
  g.rotateZ(-Math.PI / 2);
  return g;
}

function tube(r, len, seg = 24) {
  const g = new THREE.CylinderGeometry(r, r, len, seg);
  g.rotateZ(Math.PI / 2);
  return g;
}

function arcSeg(inner, outer, a0, a1, depth) {
  const sh = new THREE.Shape();
  sh.absarc(0, 0, outer, a0, a1, false);
  sh.absarc(0, 0, inner, a1, a0, true);
  sh.closePath();
  const g = new THREE.ExtrudeGeometry(sh, { depth, bevelEnabled: false, curveSegments: 12 });
  g.translate(0, 0, -depth / 2);
  g.rotateY(Math.PI / 2);
  return g;
}

const M = (geo, mat, x = 0, y = 0, z = 0) => {
  const m = new THREE.Mesh(geo, mat);
  m.position.set(x, y, z);
  return m;
};

function build() {
  const root = new THREE.Group();
  const items = [];
  const partInfo = [];
  const pinGroups = [];

  const reg = (g, id, base, dir, delay, kind, tilt, parent) => {
    g.name = id;
    g.userData = {
      part: id,
      base: new THREE.Vector3(base[0], base[1], base[2]),
      dir: new THREE.Vector3(dir[0], dir[1], dir[2]),
      delay, kind, tilt, parent, cur: 0,
    };
    g.position.set(base[0], base[1], base[2]);
    items.push(g);
  };
  const mkPart = (id, base, dir, delay, kind, tilt, shell) => {
    const g = new THREE.Group();
    reg(g, id, base, dir, delay, kind, tilt, null);
    root.add(g);
    const mats = makeMats();
    partInfo.push({ id, shell, mats: Object.values(mats), h: 0 });
    return { g, mats, id };
  };
  const mkChild = (p, id, dir, delay, tilt) => {
    const g = new THREE.Group();
    reg(g, id, [0, 0, 0], dir, delay, 'slide', tilt, p.id);
    p.g.add(g);
    return g;
  };

  // BARREL
  {
    const p = mkPart('barrel', [0.05, 0, 0], [0, -0.3, 0], 0, 'hinge', 0, true);
    const m = p.mats;
    const shell = mkChild(p, 'barrel_shell', [0, -0.12, 0], 0.1, 0.1);
    shell.scale.set(1, 0.9, 0.5);
    shell.add(M(lathe([[0.072, -0.21], [0.09, -0.21], [0.095, -0.2], [0.095, 0.2], [0.09, 0.21], [0.072, 0.21], [0.072, -0.21]], 40), m.ink));
    shell.add(M(lathe([[0.094, -0.15], [0.099, -0.15], [0.099, -0.11], [0.094, -0.11]], 40), m.mag));
    shell.add(M(lathe([[0.094, 0.11], [0.099, 0.11], [0.099, 0.15], [0.094, 0.15]], 40), m.mag));
    shell.add(M(roundedBox(0.1, 0.03, 0.05, 0.008, 0.005), m.ink, 0, 0.105, 0));
    const bear = mkChild(p, 'barrel_bearing', [0, -0.06, 0], 0.25, -0.15);
    bear.scale.set(1, 0.9, 0.5);
    [-0.15, 0.15].forEach((x) =>
      bear.add(M(lathe([[0.057, -0.025], [0.071, -0.025], [0.071, 0.025], [0.057, 0.025], [0.057, -0.025]], 32), m.steel, x))
    );
  }

  // BOW
  {
    const p = mkPart('bow', [-0.55, 0, 0], [-0.35, 0.05, 0], 0.05, 'lift', -0.25, true);
    const m = p.mats;
    p.g.add(M(ringGeo(0.275, 0.09, 0.06, 0.012), m.ink));
    p.g.add(M(ringGeo(0.245, 0.115, 0.07, 0.015), m.pink));
    const ring = mkChild(p, 'bow_ring', [-0.12, 0.06, 0], 0.15, 0.4);
    ring.add(M(new THREE.TorusGeometry(0.103, 0.016, 12, 40), m.mag));
    const face = mkChild(p, 'bow_face', [-0.06, 0.12, 0], 0.3, -0.3);
    face.add(M(ringGeo(0.2, 0.125, 0.012, 0.003), m.cream, 0, 0, 0.038));
    const pg = new THREE.CylinderGeometry(0.024, 0.024, 0.02, 20);
    pg.rotateX(Math.PI / 2);
    face.add(M(pg, m.lime, 0, 0.162, 0.05));
  }

  // COLLAR
  {
    const p = mkPart('collar', [-0.2, 0, 0], [-0.1, 0.22, 0], 0.15, 'spin', 0.3, false);
    const m = p.mats;
    p.g.add(M(lathe([[0.056, -0.025], [0.064, -0.025], [0.064, 0.025], [0.056, 0.025], [0.056, -0.025]], 28), m.mag));
    const segs = mkChild(p, 'collar_segments', [0, 0.08, 0], 0.2, 0);
    for (let i = 0; i < 4; i++) {
      segs.add(M(arcSeg(0.064, 0.105, i * Math.PI / 2 + 0.08, (i + 1) * Math.PI / 2 - 0.08, 0.05), m.tang));
    }
  }

  // BIT
  {
    const p = mkPart('bit', [0.55, -0.05, 0], [0.35, -0.05, 0], 0.3, 'slide', 0.35, false);
    const m = p.mats;
    const body = mkChild(p, 'bit_body', [0.05, 0.05, 0], 0.1, -0.2);
    body.add(M(roundedBox(0.46, 0.3, 0.05, 0.03, 0.01), m.ink));
    body.add(M(roundedBox(0.43, 0.27, 0.065, 0.025, 0.015), m.pink));
    const teeth = mkChild(p, 'teeth', [0.08, -0.06, 0], 0.3, 0.2);
    const hs = [0.06, 0.08, 0.05, 0.075, 0.065];
    for (let i = 0; i < 5; i++) {
      const tg = new THREE.Group();
      tg.name = 'tooth_' + i;
      const h = hs[i];
      const y = -0.14 - h / 2;
      tg.add(M(roundedBox(0.066, h, 0.05, 0.008, 0.004), m.ink, (i - 2) * 0.085, y, 0));
      tg.add(M(roundedBox(0.05, h - 0.008, 0.065, 0.008, 0.004), m.pink, (i - 2) * 0.085, y + 0.002, 0));
      teeth.add(tg);
    }
  }

  // SHAFT
  {
    const p = mkPart('shaft', [0, 0, 0], [0, 0.18, 0], 0.45, 'slide', 0, false);
    const m = p.mats;
    const sl = mkChild(p, 'shaft_sleeve', [0, -0.1, 0], 0.2, 0.2);
    sl.scale.set(1, 1, 0.4);
    sl.add(M(lathe([[0.04, -0.35], [0.055, -0.35], [0.055, 0.35], [0.04, 0.35], [0.04, -0.35]], 28).translate(0.07, 0, 0), m.pink));
    sl.add(M(tube(0.058, 0.012), m.ink, -0.274));
    sl.add(M(tube(0.058, 0.012), m.ink, 0.414));
    const core = mkChild(p, 'shaft_core', [0, 0.1, 0], 0.35, -0.15);
    core.scale.set(1, 1, 0.4);
    core.add(M(tube(0.035, 0.8), m.steel, 0.1));
    p.g.userData.coreGroup = core;
  }

  // PIN STACK
  {
    const p = mkPart('pin_stack', [0.55, -0.05, 0.06], [0.2, -0.2, 0.12], 0.7, 'lift', 0.3, false);
    const m = p.mats;
    const pins = mkChild(p, 'pins', [0.04, -0.05, 0.03], 0.3, 0);
    const cyl = new THREE.CylinderGeometry(0.013, 0.013, 0.15, 14);
    const cap = new THREE.CylinderGeometry(0.018, 0.018, 0.012, 14);
    const tip = new THREE.ConeGeometry(0.013, 0.04, 12);
    tip.rotateX(Math.PI);
    for (let i = 0; i < 5; i++) {
      const g = new THREE.Group();
      g.name = 'pin_' + i;
      g.position.x = (i - 2) * 0.085;
      g.add(M(cyl, m.lime, 0, 0, 0));
      g.add(M(cap, m.ink, 0, 0.081, 0));
      g.add(M(tip, m.steel, 0, -0.095, 0));
      pins.add(g);
      pinGroups.push(g);
    }
  }

  return { root, items, partInfo, pinGroups };
}

export default function BruteKeyModel({ stateRef }) {
  const built = useMemo(() => build(), []);
  const shaftRef = useMemo(() => built.items.find((o) => o.name === 'shaft'), [built]);

  useFrame((_, dt) => {
    const s = (stateRef && stateRef.current) || {};
    const k = 1 - Math.exp(-Math.min(dt, 0.1) * 20);
    const ex = s.explode || 0;
    const pe = s.partExplode || 0;
    const action = s.action || 0;
    const scroll = s.scroll || 0;
    const items = built.items;
    for (let i = 0; i < items.length; i++) {
      const o = items[i];
      const u = o.userData;
      const target = u.parent === null ? ex : (s.focusPart === u.parent ? pe : 0);
      u.cur += (target - u.cur) * k;
      const d = u.delay * 0.55;
      const x = Math.min(1, Math.max(0, (u.cur - d) / (1 - d)));
      const t = x * x * (3 - 2 * x);
      let lift = 0;
      if (u.kind === 'lift') lift = Math.sin(Math.PI * t) * 0.05;
      o.position.set(
        u.base.x + u.dir.x * t,
        u.base.y + u.dir.y * t + lift,
        u.base.z + u.dir.z * t
      );
      o.rotation.z = u.tilt * t;
      if (u.kind === 'hinge') {
        o.rotation.x = 1.92 * Math.min(1, t * 2);
      } else if (u.kind === 'spin') {
        o.rotation.x = scroll * Math.PI * 2 * 0.6;
      }
    }
    if (shaftRef) shaftRef.rotation.x = action * 0.8;
    const pg = built.pinGroups;
    for (let i = 0; i < pg.length; i++) {
      pg[i].position.y = 0.012 * Math.sin(action * Math.PI * 2 + i * 1.3) * action;
    }
    const dim = s.dim || 0;
    const cut = s.cutaway || 0;
    const pi = built.partInfo;
    for (let i = 0; i < pi.length; i++) {
      const info = pi[i];
      info.h += ((s.highlight === info.id ? 1 : 0) - info.h) * k;
      let op = 1 - 0.65 * dim;
      if (info.shell) op *= 1 - 0.9 * cut;
      const dw = op > 0.99;
      for (let j = 0; j < info.mats.length; j++) {
        const m = info.mats[j];
        m.opacity = op;
        m.depthWrite = dw;
        m.emissiveIntensity = 0.3 * info.h;
      }
    }
  });

  return <primitive object={built.root} />;
}
